import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ATTACHMENT_ACCEPT, ATTACHMENT_MAX_BYTES, attachmentBase, attachmentError, uploadInvoiceAttachment, retryAttachmentRequest } from "@/lib/invoiceAttachments";

export const useInvoiceAttachmentDraft = ({ invoiceId, open, enabled = true }) => {
  const [existing, setExisting] = useState([]);
  const [pending, setPending] = useState([]);
  const [removed, setRemoved] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const context = useRef({ id: null, invoiceId: null, version: 0, saved: false });
  const entries = useRef([]);
  const active = useRef(null);
  const load = useCallback(async () => {
    const draft = context.current;
    const target = invoiceId || draft.invoiceId;
    if (!target || !enabled) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(attachmentBase(target));
      if (context.current === draft && draft.active) { draft.version = data.version; setExisting(data.items); }
    } catch (error) { if (context.current === draft && draft.active) setError(attachmentError(error)); }
    finally { if (context.current === draft && draft.active) setLoading(false); }
  }, [invoiceId, enabled]);
  useEffect(() => {
    if (!open || !enabled) return;
    const draft = { id: crypto.randomUUID(), invoiceId, version: 0, saved: false, active: true };
    context.current = draft; entries.current = [];
    setExisting([]); setPending([]); setRemoved(new Set()); setError(""); setProgress(null);
    load();
    return () => {
      draft.active = false;
      active.current?.abort();
      if (draft.invoiceId && !draft.saved) api.delete(`${attachmentBase(draft.invoiceId)}/drafts/${draft.id}`).catch(() => {});
    };
  }, [open, enabled, invoiceId, load]);
  const choose = files => {
    const valid = [], failures = [];
    for (const file of files) {
      const ext = `.${file.name.split(".").pop().toLowerCase()}`;
      if (!ATTACHMENT_ACCEPT.split(",").includes(ext)) { failures.push(`${file.name}: unsupported file type.`); continue; }
      if (!file.size || file.size > ATTACHMENT_MAX_BYTES) { failures.push(`${file.name}: choose a nonempty file up to 2 GB.`); continue; }
      valid.push({ id: crypto.randomUUID(), file, uploaded: null });
    }
    entries.current = [...entries.current, ...valid]; setPending(entries.current); setError(failures.join(" "));
  };
  const removePending = id => { entries.current = entries.current.filter(entry => entry.id !== id); setPending(entries.current); };
  const toggleRemoval = id => setRemoved(previous => { const next = new Set(previous); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const commit = async savedInvoiceId => {
    if (!enabled) return;
    const draft = context.current; draft.invoiceId = savedInvoiceId;
    if (!entries.current.length && !removed.size) { draft.saved = true; return; }
    const controller = new AbortController(); active.current = controller; setError("");
    try {
      for (let index = 0; index < entries.current.length; index += 1) {
        const entry = entries.current[index];
        if (entry.uploaded) continue;
        const update = value => setProgress({ name: entry.file.name, current: index + 1, total: entries.current.length, ...value });
        update({ phase: "uploading", percent: 0 });
        entry.uploaded = await uploadInvoiceAttachment(savedInvoiceId, entry, draft.id, controller.signal, update);
        setPending([...entries.current]);
      }
      await retryAttachmentRequest(() => api.post(`${attachmentBase(savedInvoiceId)}/commit`, { draft_id: draft.id, version: draft.version, add_ids: entries.current.map(entry => entry.uploaded.id), remove_ids: [...removed] }, { signal: controller.signal }), controller.signal);
      draft.saved = true;
    } catch (error) { setError(attachmentError(error)); throw error; }
    finally { setProgress(null); active.current = null; }
  };
  return { existing, pending, removed, loading, error, progress, choose, removePending, toggleRemoval, commit, refresh: load };
};